import React, {useEffect, useState} from 'react';
import {Controller} from "react-hook-form";
import debounce from 'lodash/debounce'
import {cloakingDomainServices} from "../../service/cloakingDomainService";
import {Autocomplete, Box, TextField} from "@mui/material";
import {Info} from "@mui/icons-material";
import {CustomTooltip} from "../landings/LandingForm";

export default function CloakingDomainSelect({control, errors}) {
    const [open, setOpen] = useState(false);
    const [options, setOptions] = useState([]);
    const [search, setSearch] = useState("");
    const [loading, setLoading] = useState(false);
    const [requestError, setRequestError] = useState('');

    useEffect(() => {
        async function fetchData() {
            try {
                setLoading(true);
                setRequestError('');
                const response = await cloakingDomainServices?.read(search);
                setOptions(
                    response?.cloakingDomains?.map(item => {
                        return {
                            value: item?.id,
                            label: item?.domain
                        }
                    })
                );
            } catch (e) {
                setRequestError('Unable to load cloaking domains. Close and reopen the list to retry.');
            } finally {
                setLoading(false);
            }
        }

        if (open && (search?.length === 0 || search?.length > 0)) {
            fetchData();
        }

    }, [search, open]);

    useEffect(() => {
        if (!open) {
            setOptions([]);
            setSearch("");
        }
    }, [open]);

    const handleSearch = debounce(searchTerm => {
        setSearch(searchTerm);
    }, 800);

    return (
        <Controller
            render={({field: {onChange, ...props}, fieldState}) => (
                <Autocomplete
                    sx={{paddingTop: "4px"}}
                    id="cloaking-domain-select"
                    open={open}
                    onOpen={() => {
                        setOpen(true);
                    }}
                    onClose={() => {
                        setOpen(false);
                    }}
                    isOptionEqualToValue={(option, value) => {
                        if (value === null || value === "" || value === 0) {
                            return true;
                        }
                        return option.label === value.label;
                    }}
                    getOptionLabel={(option) => {
                        if (option === null || option === "" || option === 0) {
                            return "";
                        }
                        return option?.label;
                    }}
                    options={options}
                    loading={loading}
                    renderInput={params => (
                        <Box display="flex" flexDirection="column">
                            <Box display="flex" flexDirection="row" flexWrap="noWrap">
                                <Box sx={{fontSize: "14px", fontWeight: 600, color: "text.primary"}}>
                                    Cloaking domain *
                                </Box>
                                <Box>
                                    <CustomTooltip
                                        disableFocusListener
                                        title="This domain will be applied to hide the referrer info."
                                        sx={{top: 2}}
                                    >
                                        <Info color={"primary"} fontSize={"small"}/>
                                    </CustomTooltip>
                                </Box>
                            </Box>
                            <Box>
                                <TextField
                                    {...params}
                                    error={!!fieldState.error || !!requestError}
                                    helperText={fieldState.error?.message || requestError || undefined}
                                    placeholder="Select a cloaking domain"
                                    variant="outlined"
                                    margin="dense"
                                    size="small"
                                    value={search}
                                    onChange={e => handleSearch(e.target.value)}
                                    slotProps={{
                                        htmlInput: {...params.inputProps, 'aria-label': 'Cloaking domain'},
                                        inputLabel: {
                                            shrink: true
                                        }
                                    }}
                                />
                            </Box>
                        </Box>
                    )}
                    onChange={(e, data) => {setRequestError(''); onChange(data);}}
                    {...props}
                />
            )}
            rules={{required: 'Please select a cloaking domain for double meta refresh.', validate: value => value?.value > 0 || 'Please select a cloaking domain for double meta refresh.'}}
            name="cloakingDomain"
            control={control}
            defaultValue={null}
            onChange={([, data]) => data}
        />
    );
}
